export const LITRES_PAR_M3 = 1000;

/**
 * Consommation entre deux relevés consécutifs (en m3), en gérant un reset du compteur
 * (le compteur repart d'une petite valeur -> la différence brute est négative).
 */
export function computeConsumption(previous, current) {
  if (!previous) return 0;
  const raw = current.compteur - previous.compteur;
  if (raw >= 0) return raw;
  return current.compteur;
}

/** Transforme les relevés bruts en série { timestamp, consumption } (consumption en m3) */
export function toConsumptionSeries(readings) {
  const sorted = [...readings].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
  return sorted.map((reading, index) => ({
    timestamp: reading.timestamp,
    consumption: computeConsumption(sorted[index - 1], reading),
  }));
}

/** valeurM3 est déjà en m3 (unité native du back) */
export function convertVolume(valeurM3, unit) {
  return unit === 'litre' ? valeurM3 * LITRES_PAR_M3 : valeurM3;
}

function inRange(date, start, end) {
  const d = new Date(date).getTime();
  return (
    d >= new Date(start).setHours(0, 0, 0, 0) &&
    d <= new Date(end).setHours(23, 59, 59, 999)
  );
}

const MOIS = [
  "Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin",
  "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc.",
];

export function aggregateByDay(series, start, end, unit) {
  const buckets = {};
  series
    .filter((p) => inRange(p.timestamp, start, end))
    .forEach((p) => {
      const key = new Date(p.timestamp).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
      });
      buckets[key] = (buckets[key] || 0) + p.consumption;
    });
  return Object.entries(buckets).map(([name, valeurM3]) => ({
    name,
    Volume: parseFloat(convertVolume(valeurM3, unit).toFixed(3)),
  }));
}

export function aggregateByMonth(series, start, end, unit) {
  const buckets = {};
  series
    .filter((p) => inRange(p.timestamp, start, end))
    .forEach((p) => {
      const d = new Date(p.timestamp);
      const key = `${MOIS[d.getMonth()]} ${d.getFullYear()}`;
      buckets[key] = (buckets[key] || 0) + p.consumption;
    });
  return Object.entries(buckets).map(([name, valeurM3]) => ({
    name,
    Volume: parseFloat(convertVolume(valeurM3, unit).toFixed(3)),
  }));
}

export function aggregateByYear(series, start, end, unit) {
  const buckets = {};
  series
    .filter((p) => inRange(p.timestamp, start, end))
    .forEach((p) => {
      const key = `${new Date(p.timestamp).getFullYear()}`;
      buckets[key] = (buckets[key] || 0) + p.consumption;
    });
  return Object.entries(buckets).map(([name, valeurM3]) => ({
    name,
    Volume: parseFloat(convertVolume(valeurM3, unit).toFixed(3)),
  }));
}

/**
 * Totaux de consommation par jour (en m3), sur TOUT l'historique disponible,
 * indépendamment des filtres de dates des graphiques.
 * Sert à calculer les dépassements de seuil.
 */
export function getDailyTotals(series) {
  const buckets = {};
  series.forEach((p) => {
    const d = new Date(p.timestamp);
    const key = d.toISOString().slice(0, 10); // YYYY-MM-DD, triable
    if (!buckets[key]) {
      buckets[key] = { dateKey: key, date: new Date(d.setHours(0, 0, 0, 0)), totalM3: 0 };
    }
    buckets[key].totalM3 += p.consumption;
  });
  return Object.values(buckets).sort((a, b) => b.date - a.date);
}